import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface NotificationOrder {
  table?: {
    tableNo?: number;
  };
  items?: unknown[];
}

interface NotificationInvoice {
  amount?: string | number;
  orders?: Array<{
    table?: {
      tableNo?: number;
    };
  }>;
}

export type NotificationType = 
  | 'new_order' 
  | 'order_updated' 
  | 'order_cancelled'
  | 'invoice_requested'
  | 'invoice_paid';

export interface Notification {
  id: string;
  type: NotificationType;
  order?: NotificationOrder;
  invoice?: NotificationInvoice;
  tableNo?: number;
  read: boolean;
  createdAt: string;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  soundEnabled: boolean;
}

const getInitialSoundEnabled = (): boolean => {
  const stored = localStorage.getItem('notificationSound');
  return stored !== 'false';
};

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  soundEnabled: getInitialSoundEnabled(),
};

export const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (
      state,
      action: PayloadAction<Omit<Notification, 'id' | 'read' | 'createdAt'>>
    ) => {
      const notification: Notification = {
        ...action.payload,
        id: crypto.randomUUID(),
        read: false,
        createdAt: new Date().toISOString(),
      };
      state.notifications.unshift(notification);
      state.unreadCount += 1;
      // Keep only last 50 notifications
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50);
      }
    },
    markAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find((n) => n.id === action.payload);
      if (notification && !notification.read) {
        notification.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllAsRead: (state) => {
      state.notifications.forEach((n) => (n.read = true));
      state.unreadCount = 0;
    },
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
    },
    toggleSound: (state) => {
      state.soundEnabled = !state.soundEnabled;
      localStorage.setItem('notificationSound', String(state.soundEnabled));
    },
    setSoundEnabled: (state, action: PayloadAction<boolean>) => {
      state.soundEnabled = action.payload;
      localStorage.setItem('notificationSound', String(action.payload));
    },
  },
});

export const {
  addNotification,
  markAsRead,
  markAllAsRead,
  clearNotifications,
  toggleSound,
  setSoundEnabled,
} = notificationSlice.actions;
